import { Anchor, Badge, Group, Stack, Tabs, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { CrudSection } from '../components/CrudSection';
import { describeWeekdays } from '../components/WeekdaysField';
import { useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumOptions } from '../lib/enums';
import { formatMoney, formatRelative } from '../lib/format';
import { BillsThatDisagreeSection } from './suppliers/BillsThatDisagreeSection';
import {
  ALL_CONTACTS_PATH,
  ReachLink,
  type SupplierContact,
  waysToReach,
  whoToCall,
} from './suppliers/contacts';
import { DeliveriesSection } from './suppliers/DeliveriesSection';
import { OrdersSection } from './suppliers/OrdersSection';
import { PayablesSection } from './suppliers/PayablesSection';
import { PaymentsSection } from './suppliers/PaymentsSection';
import { ShippingChargesSection } from './suppliers/ShippingChargesSection';
import { SupplierScorecardSection } from './suppliers/SupplierScorecardSection';

/**
 * A supplier row answers questions that do not live on the supplier record — what is outstanding,
 * what is on order. Both come from collections the page already loads, so the cells read from the
 * shared query cache rather than each firing their own request.
 */
function useOutstanding(supplierId: string) {
  const payables = useQuery({
    queryKey: listKey('/admin/v1/suppliers/payables'),
    queryFn: () =>
      rawApi<
        {
          invoice: {
            supplierId: string;
            currency: string;
            totalMinor: number;
            paidMinor: number;
            status: string;
          };
          outstandingMinor: number;
        }[]
      >('GET', '/admin/v1/suppliers/payables'),
    staleTime: 30_000,
  });
  const byCurrency = new Map<string, number>();
  for (const row of payables.data ?? []) {
    if (row.invoice.supplierId !== supplierId) continue;
    /*
     * The server's figure, never `totalMinor − paidMinor`. That subtraction is wrong by the whole
     * of any credit the vendor has issued, and wrong in the direction of "you still owe this": a
     * bill with L 1,350.00 credited back read L 4,500.00 in this cell while Bills to pay, built on
     * the same request one screen away, read L 3,150.00. `outstandingMinor` is billed less paid
     * LESS CREDITED, worked out in the one place that reconciles all four figures.
     */
    if (row.outstandingMinor > 0) {
      byCurrency.set(
        row.invoice.currency,
        (byCurrency.get(row.invoice.currency) ?? 0) + row.outstandingMinor,
      );
    }
  }
  return { byCurrency, isLoading: payables.isLoading };
}

function OwedCell({ supplierId }: { supplierId: string }) {
  const { byCurrency, isLoading } = useOutstanding(supplierId);
  if (isLoading)
    return (
      <Text size="sm" c="dimmed">
        …
      </Text>
    );
  if (byCurrency.size === 0) {
    return (
      <Text size="sm" c="dimmed">
        Nothing
      </Text>
    );
  }
  return (
    <Stack gap={0}>
      {[...byCurrency.entries()].map(([currency, owed]) => (
        <Text key={currency} size="sm" fw={700} c="red.7">
          {formatMoney(owed, currency)}
        </Text>
      ))}
    </Stack>
  );
}

function OpenOrdersCell({ supplierId }: { supplierId: string }) {
  const orders = useQuery({
    queryKey: listKey('/admin/v1/suppliers/purchase-orders'),
    queryFn: () =>
      rawApi<{ supplierId: string; status: string; orderedAt?: string | null }[]>(
        'GET',
        '/admin/v1/suppliers/purchase-orders',
      ),
    staleTime: 30_000,
  });
  // Anything sent but not fully received is still owed to you — that is what "on order" means to
  // someone deciding whether to chase or re-order.
  const open = (orders.data ?? []).filter(
    (o) => o.supplierId === supplierId && (o.status === 'sent' || o.status === 'partiallyReceived'),
  );
  if (orders.isLoading)
    return (
      <Text size="sm" c="dimmed">
        …
      </Text>
    );
  if (open.length === 0)
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  // How long the oldest one has been waiting, which is the thing worth chasing. It used to say
  // when the order was expected — a date nothing in this system has ever set, so the line never
  // appeared at all and the cell was a bare count.
  const oldest = open
    .map((o) => o.orderedAt)
    .filter((at): at is string => Boolean(at))
    .sort()[0];
  return (
    <div>
      <Badge variant="light" color="yellow">
        {open.length} {open.length === 1 ? 'order' : 'orders'}
      </Badge>
      {oldest && (
        <Text size="xs" c="dimmed">
          sent {formatRelative(oldest)}
        </Text>
      )}
    </div>
  );
}

/**
 * Who to call at this vendor, as one line.
 *
 * A vendor can have several people — Madeyso's owner plus the people at each of its own counters —
 * and this is a table, so the cell shows the one to call and says quietly that there are others
 * rather than becoming a list. The name is here because a number with nobody's name against it
 * tells a manager nothing about who is going to pick up.
 */
function ReachCell({
  supplier,
  contacts,
  state,
}: {
  supplier: Record<string, unknown>;
  contacts: SupplierContact[];
  state: 'loading' | 'error' | 'ready';
}) {
  // Until the people have arrived this cell knows nothing — and flashing "Add someone" against a
  // vendor who has four of them is worse than a moment of nothing.
  if (state === 'loading') {
    return (
      <Text size="sm" c="dimmed">
        …
      </Text>
    );
  }

  const theirs = contacts.filter((c) => c.supplierId === supplier.id && c.active);
  const first = whoToCall(theirs);

  if (first) {
    const way = waysToReach(first)[0];
    return (
      <Stack gap={0}>
        <Text size="xs" c="dimmed">
          {first.name}
          {first.branchName ? ` · ${first.branchName}` : ''}
        </Text>
        {way ? (
          <ReachLink way={way} />
        ) : (
          <Text size="sm" c="dimmed">
            No number yet
          </Text>
        )}
        {theirs.length > 1 && (
          <Text size="xs" c="dimmed">
            +{theirs.length - 1} {theirs.length === 2 ? 'other' : 'others'}
          </Text>
        )}
      </Stack>
    );
  }

  // Nobody on file yet: the vendor's own columns, which is where every number lived before people
  // could be recorded separately. Every one of those was copied across, so this is for rows written
  // straight to the API since.
  const legacy = waysToReach({
    whatsapp: typeof supplier.whatsapp === 'string' ? supplier.whatsapp : null,
    phone: typeof supplier.phone === 'string' ? supplier.phone : null,
  })[0];
  if (legacy) {
    return (
      <Stack gap={0}>
        {Boolean(supplier.contactName) && (
          <Text size="xs" c="dimmed">
            {String(supplier.contactName)}
          </Text>
        )}
        <ReachLink way={legacy} />
      </Stack>
    );
  }

  // Nothing loaded, so nothing can honestly be said: inviting someone to add a contact they may
  // already have is how a vendor ends up with the same person on file twice.
  if (state === 'error') {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  // An empty cell that only says "no number" leaves the manager to work out where to put one.
  return (
    <Anchor component={Link} to={`/suppliers/${String(supplier.id)}`} size="sm" c="dimmed">
      Add someone
    </Anchor>
  );
}

/**
 * The tabs, in the address. Reaching the bills used to mean opening Suppliers and clicking across:
 * the dashboard's "Bills to pay · L 2,450" tile handed the reader a figure and then the vendor
 * list, and a supplier's page could not send anyone back to the tab they had come from.
 */
const TABS = ['suppliers', 'pos', 'deliveries', 'shipping', 'payables', 'performance'] as const;

export function SuppliersPage() {
  const locationId = useLocationId();
  const [tab, setTab] = useTabInUrl(TABS, 'suppliers');
  // Every vendor's people in one request. Asking per row would be one round trip per supplier to
  // fill in a single column — noticeable on a phone behind the bar, and pointless when the whole
  // collection is a few dozen rows.
  const contactsQuery = useQuery({
    queryKey: listKey(ALL_CONTACTS_PATH),
    queryFn: () => rawApi<SupplierContact[]>('GET', ALL_CONTACTS_PATH),
    staleTime: 30_000,
  });
  const contacts = contactsQuery.data ?? [];
  const contactsState = contactsQuery.isLoading ? 'loading' : contactsQuery.isError ? 'error' : 'ready';

  return (
    <>
      <Title order={3} mb="md">
        Suppliers &amp; bills
      </Title>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        {/* In the order the goods travel: who you buy from, what you asked them for, what turned
            up, what the crossing cost, what is left to pay. */}
        <Tabs.List mb="md">
          <Tabs.Tab value="suppliers">Suppliers</Tabs.Tab>
          <Tabs.Tab value="pos">Orders</Tabs.Tab>
          <Tabs.Tab value="deliveries">Deliveries</Tabs.Tab>
          <Tabs.Tab value="shipping">Shipping over</Tabs.Tab>
          <Tabs.Tab value="payables">Bills to pay</Tabs.Tab>
          {/* Last, because it is the question asked at the end of the month rather than at eight
              in the morning: not "what do I owe" but "who is worth ordering from". */}
          <Tabs.Tab value="performance">How they do</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="deliveries">
          {/* The screen this whole tab strip depends on, and until now it was written but never
              hung anywhere: nothing in the portal could put stock on a shelf, open a supplier
              bill, or give the shipping-over screen a delivery to charge for. */}
          <DeliveriesSection locationId={locationId} />
        </Tabs.Panel>

        <Tabs.Panel value="shipping">
          {/* Not a CrudSection like its neighbours: a shipping charge is a bill AND the deliveries
              it paid for, and a form offering one value per field cannot ask the second half. */}
          <ShippingChargesSection locationId={locationId} />
        </Tabs.Panel>

        <Tabs.Panel value="suppliers">
          <CrudSection
            title="Suppliers"
            entityNoun="supplier"
            description="Everyone you buy from, plus the ferry and freight companies that bring goods over. Mainland orders need a crossing — what that costs is recorded under Shipping over, because it is the carrier who bills you for it."
            listPath="/admin/v1/suppliers/suppliers"
            updatePathTemplate="/admin/v1/suppliers/suppliers/{id}"
            defaults={{ locationId }}
            rowLabel={(r) => String(r.name ?? 'this supplier')}
            emptyMessage="No suppliers yet. Add the first one to start recording orders and bills."
            columns={[
              {
                accessor: 'name',
                title: 'Supplier',
                // The mainland marker sits here because "will this need a crossing?" belongs to the
                // vendor's identity rather than deserving a column of its own titled after a
                // database field. Who you speak to now lives in the next column, where the number
                // is: two columns naming two different people was the old shape of this table.
                render: (r) => (
                  <Group gap={6} wrap="nowrap">
                    <Anchor component={Link} to={`/suppliers/${String(r.id)}`} fw={600}>
                      {String(r.name ?? '—')}
                    </Anchor>
                    {!r.island && (
                      <Badge size="xs" variant="light" color="blue" title="Mainland — orders need a crossing">
                        Mainland
                      </Badge>
                    )}
                    {(r.kind === 'freight' || r.kind === 'both') && (
                      <Badge size="xs" variant="light" color="grape">
                        Carrier
                      </Badge>
                    )}
                  </Group>
                ),
              },
              {
                accessor: '__reach',
                title: 'Reach them',
                // Actionable rather than informational: on Roatán orders go out on WhatsApp, so the
                // number is something you tap, not something you copy.
                render: (r) => <ReachCell supplier={r} contacts={contacts} state={contactsState} />,
              },
              {
                accessor: '__owed',
                title: 'You owe',
                // The number a manager opens this screen for. It is not on the supplier record —
                // it is the sum of their unpaid bills — which is exactly why a table that only ever
                // shows columns of one row could not answer it.
                render: (r) => <OwedCell supplierId={String(r.id)} />,
              },
              {
                accessor: '__orders',
                title: 'On order',
                render: (r) => <OpenOrdersCell supplierId={String(r.id)} />,
              },
              {
                accessor: 'deliveryDays',
                title: 'They come',
                // A vendor whose days nobody has recorded says so rather than showing a dash: the
                // blank is the thing to fix, and it is one click away on this very row.
                render: (r) =>
                  describeWeekdays(r.deliveryDays) ?? (
                    <Text size="sm" c="dimmed">
                      Not recorded
                    </Text>
                  ),
              },
              {
                accessor: 'paymentTermsDays',
                title: 'Terms',
                render: (r) => (r.paymentTermsDays ? `${String(r.paymentTermsDays)} days` : 'On delivery'),
              },
            ]}
            // No name or number here on purpose. A vendor can be several people at several of
            // their own places, and four boxes on this form can only ever hold one of them — which
            // is the limitation this screen was changed to remove. People are added on the
            // vendor's own page, where they can be told apart.
            fields={[
              { name: 'name', label: 'Supplier name', type: 'text', required: true },
              {
                name: 'rtn',
                label: 'RTN',
                type: 'text',
                nullable: true,
                description: 'Their tax number, as it appears on the invoices they give you',
              },
              {
                // Not "What they are": a label naming the shape of the record rather than the
                // decision being made. The answer changes one thing only — whether this vendor can
                // be picked as the one who brought a load across — so the label says that job.
                name: 'kind',
                label: 'What they do for you',
                type: 'select',
                options: enumOptions('supplierKind'),
                defaultValue: 'goods',
                description: 'Only the ones that bring goods over can be picked on a shipping charge',
              },
              {
                name: 'island',
                label: 'Based on the island',
                type: 'boolean',
                description: 'Leave unticked for anyone shipping over from the mainland',
              },
              {
                name: 'invoiceCurrency',
                label: 'They bill you in',
                type: 'select',
                options: enumOptions('currency'),
              },
              {
                name: 'minimumOrderMinor',
                label: 'Minimum order',
                type: 'number',
                unit: 'money',
                nullable: true,
                description: 'The smallest order they will deliver, if they have one',
              },
              {
                name: 'paymentTermsDays',
                label: 'Days you have to pay',
                type: 'number',
                description: 'Leave at 0 if you pay on delivery',
              },
              {
                name: 'defaultLeadTimeDays',
                label: 'Usual days to deliver',
                type: 'number',
                nullable: true,
                description: 'Roughly how long between placing an order and it arriving',
              },
              {
                // The fact that decides whether an order goes out this afternoon. On an island
                // where the boat is the supply chain, missing Friday's run is four days of no
                // limes — and until now there was nowhere to write down when the run is.
                name: 'deliveryDays',
                label: 'Days they come',
                type: 'weekdays',
                description: 'The days their truck or boat actually turns up',
                placeholder: 'Nobody has written down when they come, so an order cannot be timed.',
              },
              { name: 'notes', label: 'Notes', type: 'text', nullable: true },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="pos">
          <OrdersSection locationId={locationId} />
        </Tabs.Panel>

        <Tabs.Panel value="performance">
          <SupplierScorecardSection />
        </Tabs.Panel>

        <Tabs.Panel value="payables">
          {/* Above the bills, because a bill that does not agree with its delivery is a bill you
              are about to pay the wrong amount of — and because this is where the owner already
              comes to look at what he owes. It renders nothing at all when nothing disagrees. */}
          <BillsThatDisagreeSection />
          <PayablesSection />
          {/* Below the bills, because a payment is what takes a bill off that list — and because a
              payment recorded wrong is only ever found by somebody who came here to look at what
              he owes and could not make the figure agree. */}
          <PaymentsSection />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}
